// src/components/ChatInput.js
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ArrowCircleUpSharpIcon from "@mui/icons-material/ArrowCircleUpSharp";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import "../../styles/Betsy.css";
import ClearIcon from "@mui/icons-material/Clear";
import { validateToken } from "../../pages/ValidateToken";
import { urls } from "../URLs";

const ChatInput = ({ input, setInput, onSend, isTyping }) => {
  const searchRef = useRef(null);
  const [validToken, setValidToken] = useState(null);

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem("token");
      console.log("Token from localStorage:", token);

      if (!token) {
        setValidToken(false);

        localStorage.removeItem("username");
        return;
      }

      const { valid, user } = await validateToken(token);
      console.log("Validation Response:", { valid, user });

      if (!valid) {
        setValidToken(false);

        localStorage.removeItem("username");
        return;
      }

      setValidToken(true);
    };

    checkToken();
  }, []);

  const handleSend = () => {
    if (input.trim() && !isTyping) {
      onSend(input);
      setInput("");
    }
  };

  const filteredUrls = urls.filter((item) =>
    item.name.toLowerCase().includes(input.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setInput(""); // Clear search
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Box className="chat-input-field">
      {!validToken ? (
        <Box ref={searchRef} sx={{ position: "relative", width: "100%" }}>
          <TextField
            fullWidth
            placeholder="Type a keyword here to run a general search on UtahData..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {input && (
                    <IconButton onClick={() => setInput("")} edge="end">
                      <ClearIcon sx={{ color: "#fff" }} />
                    </IconButton>
                  )}

                  {isTyping ? (
                    <IconButton edge="end" disabled>
                      <StopCircleOutlinedIcon sx={{ color: "#fff" }} />
                    </IconButton>
                  ) : (
                    <IconButton onClick={handleSend} edge="end">
                      <ArrowCircleUpSharpIcon sx={{ color: "#fff" }} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: "#303030",
              border: "1px solid #aaa",
              borderRadius: "25px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "25px",
              },
              "& .MuiInputBase-input": {
                color: "#fff",
                fontFamily: '"JetBrains Mono", monospace',
              },
              "& .MuiInputBase-input::placeholder": {
                color: "gray",
                opacity: 1,
              },
            }}
          />

          {input && (
            <List
              sx={{
                position: "absolute",
                bottom: "55px",
                left: 0,
                width: "100%",
                background: "#fff",
                boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                borderRadius: "8px",
                maxHeight: "350px",
                overflowY: "auto",
                zIndex: 1000,
              }}
            >
              {filteredUrls.length > 0 ? (
                filteredUrls.map((item, index) => (
                  <ListItem
                    button
                    key={index}
                    component="a"
                    href={item.path}
                    sx={{
                      "&:hover": {
                        background: "#f5f5f5",
                      },
                    }}
                  >
                    <ListItemText primary={item.name} />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No results found" />
                </ListItem>
              )}
            </List>
          )}
        </Box>
      ) : (
        <TextField
          fullWidth
          placeholder="Ask Anything..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {input && (
                  <IconButton onClick={() => setInput("")} edge="end">
                    <ClearIcon sx={{ color: "#fff" }} />
                  </IconButton>
                )}

                {isTyping ? (
                  <IconButton edge="end" color="primary" disabled={isTyping}>
                    <StopCircleOutlinedIcon sx={{ color: "#fff" }} />
                  </IconButton>
                ) : (
                  <IconButton onClick={handleSend} edge="end" color="primary">
                    <ArrowCircleUpSharpIcon sx={{ color: "#fff" }} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: "#303030",
            border: "1px solid #aaa",
            borderRadius: "25px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "25px",
            },
            "& .MuiInputBase-input": {
              color: "#fff",
              fontFamily: '"JetBrains Mono", monospace',
            },
            "& .MuiInputBase-input::placeholder": {
              color: "gray",
              opacity: 1,
            },
          }}
        />
      )}
      <p className="helpertext1">
        * BETSY can make mistakes, please check and validate responses. ©
        Copyright 2026. All Rights Reserved.
        <br />
        <span className="helpertext2">
          BETSY is currently being trained on UTahData.org data
        </span>
      </p>
    </Box>
  );
};

export default ChatInput;
